import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createUserLibItem,
  deleteUserLibItem,
  fetchUserLib,
  fmtRelative,
} from "../lib/api";
import { Copy, Library, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function UserLibraryWidget({ topicSlug, topicName }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const [note, setNote] = useState("");

  const { data: items = [] } = useQuery({
    queryKey: ["user-lib", topicSlug],
    queryFn: () => fetchUserLib(topicSlug),
    refetchInterval: 30000,
  });

  const save = useMutation({
    mutationFn: () =>
      createUserLibItem({ topic_slug: topicSlug, text: text.trim(), note }),
    onSuccess: () => {
      setText("");
      setNote("");
      qc.invalidateQueries({ queryKey: ["user-lib", topicSlug] });
      toast.success("SAVED_TO_LIBRARY");
    },
    onError: (e) =>
      toast.error("SAVE_FAILED", { description: e?.message || "" }),
  });

  const del = useMutation({
    mutationFn: (id) => deleteUserLibItem(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user-lib", topicSlug] }),
  });

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.success("COPIED");
    } catch {
      toast.error("COPY_FAILED");
    }
  };

  const sendToBot = (item) => {
    // store a reference in sessionStorage and route to /bot
    const ref = {
      kind: "user_lib",
      ref_id: item.id,
      label: `[saved · ${topicName}]`,
      text: item.text,
    };
    const existing = JSON.parse(sessionStorage.getItem("mindstein_pending_refs") || "[]");
    existing.push(ref);
    sessionStorage.setItem("mindstein_pending_refs", JSON.stringify(existing));
    window.location.href = "/bot";
  };

  return (
    <section
      data-testid="user-library-widget"
      className="border border-[#27272A] bg-[#0A0A0A]"
    >
      <div className="px-3 py-2 border-b border-[#27272A] flex items-center gap-2">
        <Library size={12} className="text-amber-400" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-mono">
          MY_LIBRARY · {topicName}
        </span>
        <span className="ml-auto text-[10px] font-mono text-zinc-500 tabnum">
          {items.length} SAVED
        </span>
      </div>
      <div className="p-3 grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-7">
          <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono">
            PASTE_OR_TYPE
          </label>
          <textarea
            data-testid="user-library-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={`Paste an idea or thought about ${topicName.toLowerCase()}…`}
            className="mt-1 w-full h-24 bg-[#0E0E0E] border border-[#27272A] focus:border-amber-400 outline-none p-2 text-[12px] text-white font-mono resize-y"
          />
          <input
            data-testid="user-library-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="optional note (e.g. follow-up next week)"
            className="mt-2 w-full h-8 bg-[#0E0E0E] border border-[#27272A] focus:border-amber-400 outline-none px-2 text-[12px] text-white font-mono"
          />
          <div className="mt-2 flex gap-2">
            <button
              data-testid="user-library-save-button"
              onClick={() => save.mutate()}
              disabled={!text.trim() || save.isPending}
              className="h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-white bg-white text-black hover:bg-amber-300 hover:border-amber-300 disabled:opacity-50"
            >
              {save.isPending ? "SAVING…" : "SAVE_TO_LIBRARY"}
            </button>
            <button
              data-testid="user-library-copy-button"
              onClick={() => copy(text)}
              disabled={!text.trim()}
              className="h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-[#141414] disabled:opacity-50"
            >
              <Copy size={11} className="inline mr-1" /> COPY
            </button>
            <Link
              to="/bot"
              data-testid="user-library-bot-link"
              className="h-8 px-3 text-[11px] font-mono uppercase tracking-wider border border-purple-400 text-purple-200 hover:bg-purple-400 hover:text-black flex items-center gap-1"
            >
              <Send size={11} /> OPEN_BOT
            </Link>
          </div>
        </div>
        <div className="md:col-span-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1">
            SAVED · {topicName}
          </div>
          {items.length === 0 ? (
            <div className="border border-[#27272A] grid-bg p-4 text-[11px] text-zinc-500 font-mono uppercase tracking-wider text-center">
              NO_SAVED_ITEMS_YET
            </div>
          ) : (
            <ul className="max-h-60 overflow-y-auto border border-[#27272A]">
              {items.map((it) => (
                <li
                  key={it.id}
                  data-testid={`user-library-item-${it.id}`}
                  className="border-b border-[#161616] p-2 hover:bg-[#0E0E0E]"
                >
                  <div className="text-[11px] text-white whitespace-pre-wrap line-clamp-3">
                    {it.text}
                  </div>
                  {it.note && (
                    <div className="text-[10px] text-amber-300 font-mono mt-1">
                      ↳ {it.note}
                    </div>
                  )}
                  <div className="flex items-center mt-1 gap-1 text-[10px] font-mono text-zinc-500">
                    <span>{fmtRelative(it.created_at)}</span>
                    <button
                      onClick={() => copy(it.text)}
                      className="ml-auto px-1 hover:text-white"
                      title="copy"
                    >
                      <Copy size={11} />
                    </button>
                    <button
                      onClick={() => sendToBot(it)}
                      className="px-1 hover:text-purple-300"
                      title="send to bot"
                    >
                      <Send size={11} />
                    </button>
                    <button
                      onClick={() => del.mutate(it.id)}
                      className="px-1 hover:text-rose-400"
                      title="delete"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}
